import React from 'react';

export interface RulerSvgProps {
  width?: number;
}

// Internal design space
const VW = 320;
const VH = 44;
/** Rendered height ÷ width — use this to reserve layout space without duplicating viewBox values. */
export const RULER_ASPECT_RATIO = VH / VW;
const PX = 8;  // horizontal padding
const PY = 5;  // vertical padding
const BODY_W = VW - PX * 2;
const BODY_H = VH - PY * 2;
const CM_COUNT = 15;
const CM_PX = BODY_W / CM_COUNT;

const C_BORDER = '#705DC0';
const C_FILL   = '#f5f2ff';
const C_TICK   = '#4a3a9e';
const C_LABEL  = '#2d1f7a';
const C_STRIPE = '#c4b8f0';

/**
 * Compact 15 cm ruler — viewBox-based, scales via width prop.
 */
export const RulerSvg: React.FC<RulerSvgProps> = ({ width = 240 }) => {
  const ticks: React.ReactNode[] = [];

  for (let mm = 0; mm <= CM_COUNT * 10; mm++) {
    const x = PX + (mm / 10) * CM_PX;
    const isCm  = mm % 10 === 0;
    const isMid = mm % 5 === 0 && !isCm;
    const tickH = isCm ? 16 : isMid ? 10 : 6;
    const sw    = isCm ? 1.2 : 0.7;

    ticks.push(
      <line
        key={`mm${mm}`}
        x1={x} y1={PY}
        x2={x} y2={PY + tickH}
        stroke={C_TICK}
        strokeWidth={sw}
      />
    );

    if (isCm && mm > 0 && mm < CM_COUNT * 10) {
      ticks.push(
        <text
          key={`lbl${mm}`}
          x={x}
          y={PY + BODY_H - 6}
          textAnchor="middle"
          fontSize={8}
          fontWeight="600"
          fontFamily="Arial, sans-serif"
          fill={C_LABEL}
        >
          {mm / 10}
        </text>
      );
    }
  }

  return (
    <svg
      width={width}
      viewBox={`0 0 ${VW} ${VH}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block' }}
      role="img"
      aria-label="Ruler"
    >
      <rect
        x={PX} y={PY}
        width={BODY_W} height={BODY_H}
        rx={2}
        fill={C_FILL}
        stroke={C_BORDER}
        strokeWidth={1.5}
      />
      <rect x={PX} y={PY} width={BODY_W} height={4} rx={2} fill={C_STRIPE} opacity={0.6} />
      {ticks}
      <text x={PX + 3} y={PY + BODY_H - 6} fontSize={7} fontFamily="Arial, sans-serif" fill={C_LABEL} opacity={0.6}>0</text>
      <text x={PX + BODY_W - 2} y={PY + BODY_H - 6} textAnchor="end" fontSize={7} fontFamily="Arial, sans-serif" fill={C_BORDER} opacity={0.7}>cm</text>
    </svg>
  );
};
